import React, { useCallback, useMemo, useState } from 'react';
import {
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import {
  isReportGranted,
  REPORT_ACCESS_CODES,
  REPORT_ACCESS_PERMISSIONS,
} from '../../config/reportCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { darkColors } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { getAdminAttendanceDashboard } from '../../services/attendanceService';
import { getReportCatalogue } from '../../services/reportService';
import type { AdminAttendanceDashboardMetrics } from '../../types/attendance';
import type { ReportAccess, ReportDefinition } from '../../types/report';
import { formatDate, getGreeting } from '../../utils/dateTime';
import { reportErrorMessage } from '../../utils/reportErrors';

type Props = NativeStackScreenProps<RootStackParamList, 'ReportList'>;

type SummaryTone = 'info' | 'success' | 'danger';

type SummaryDef = {
  key: keyof Pick<
    AdminAttendanceDashboardMetrics,
    'totalEmployees' | 'presentToday' | 'absent' | 'onLeave'
  >;
  label: string;
  tone: SummaryTone;
};

type CategoryIcon =
  | 'clipboard'
  | 'calendar'
  | 'people'
  | 'clock'
  | 'chart'
  | 'home'
  | 'leave'
  | 'document';

type CategoryItem = {
  id: string;
  title: string;
  icon: CategoryIcon;
  onPress: () => void;
};

const QUICK_SUMMARY: readonly SummaryDef[] = [
  { key: 'totalEmployees', label: 'Total Employees', tone: 'info' },
  { key: 'presentToday', label: 'Present Today', tone: 'success' },
  { key: 'absent', label: 'LOP Today', tone: 'danger' },
  { key: 'onLeave', label: 'On Leave', tone: 'danger' },
];

/**
 * Design category order. Early Exit is intentionally omitted (no early-exit UI).
 * `accessCode` links each entry to role report access from the backend.
 */
const DESIGN_REPORT_ORDER: readonly {
  id: string;
  title: string;
  icon: CategoryIcon;
  accessCode: string;
  reportCode?: string;
  attendanceStatus?: string;
  kind:
    | 'report'
    | 'attendance'
    | 'monthlyAttendance'
    | 'weeklyAttendance'
    | 'employeeOverallWeekly'
    | 'employeeMonthlyAttendance'
    | 'wfh'
    | 'leave';
}[] = [
  {
    id: 'daily',
    accessCode: REPORT_ACCESS_CODES.DAILY_ATTENDANCE,
    title: 'Daily Attendance Report',
    icon: 'clipboard',
    kind: 'attendance',
  },
  {
    id: 'monthly',
    accessCode: REPORT_ACCESS_CODES.MONTHLY_ATTENDANCE,
    title: 'Monthly Attendance Report',
    icon: 'calendar',
    reportCode: 'MONTHLY_ATTENDANCE_SUMMARY',
    kind: 'monthlyAttendance',
  },
  {
    id: 'weekly',
    accessCode: REPORT_ACCESS_CODES.WEEKLY_ATTENDANCE,
    title: 'Weekly Attendance Report',
    icon: 'calendar',
    kind: 'weeklyAttendance',
  },
  {
    id: 'employee-overall-weekly',
    accessCode: REPORT_ACCESS_CODES.EMPLOYEE_OVERALL_WEEKLY,
    title: 'Employee Overall Weekly Report',
    icon: 'people',
    kind: 'employeeOverallWeekly',
  },
  {
    id: 'employee-monthly-attendance',
    accessCode: REPORT_ACCESS_CODES.EMPLOYEE_MONTHLY_ATTENDANCE,
    title: 'Employee Monthly Attendance Report',
    icon: 'calendar',
    kind: 'employeeMonthlyAttendance',
  },
  {
    id: 'employee',
    accessCode: 'EMPLOYEE_DIRECTORY',
    title: 'Employee Summary Report',
    icon: 'people',
    reportCode: 'EMPLOYEE_DIRECTORY',
    kind: 'report',
  },
  {
    id: 'late',
    accessCode: REPORT_ACCESS_CODES.LATE_ARRIVAL,
    title: 'Late Arrival Report',
    icon: 'clock',
    kind: 'attendance',
    attendanceStatus: 'Late',
  },
  {
    id: 'short',
    accessCode: REPORT_ACCESS_CODES.SHORT_HOURS,
    title: 'Short Hours Report',
    icon: 'chart',
    kind: 'attendance',
    attendanceStatus: 'Short Hours',
  },
  {
    id: 'wfh',
    accessCode: 'WFH_SUMMARY',
    title: 'WFH Report',
    icon: 'home',
    reportCode: 'WFH_SUMMARY',
    kind: 'wfh',
  },
  {
    id: 'leave',
    accessCode: 'LEAVE_REGISTER',
    title: 'Leave Report',
    icon: 'leave',
    reportCode: 'LEAVE_REGISTER',
    kind: 'leave',
  },
];

const COVERED_REPORT_CODES = new Set(
  DESIGN_REPORT_ORDER.map(item => item.reportCode).filter(Boolean) as string[],
);

function toneColor(palette: ColorPalette, tone: SummaryTone): string {
  switch (tone) {
    case 'success':
      return palette.success;
    case 'danger':
      return palette.error;
    default:
      return palette.info;
  }
}

function formatMetric(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return String(value);
}

function BellIcon({ color }: { color: string }) {
  return (
    <View style={styles.bellRoot} accessibilityElementsHidden>
      <View style={[styles.bellBody, { borderColor: color }]} />
      <View style={[styles.bellClapper, { backgroundColor: color }]} />
    </View>
  );
}

function CategoryGlyph({
  icon,
  color,
}: {
  icon: CategoryIcon;
  color: string;
}) {
  if (icon === 'calendar') {
    return (
      <View style={styles.glyphRoot} accessibilityElementsHidden>
        <View style={[styles.calendarBox, { borderColor: color }]}>
          <View style={[styles.calendarTop, { backgroundColor: color }]} />
          <View style={styles.calendarDots}>
            <View style={[styles.calendarDot, { backgroundColor: color }]} />
            <View style={[styles.calendarDot, { backgroundColor: color }]} />
            <View style={[styles.calendarDot, { backgroundColor: color }]} />
          </View>
        </View>
      </View>
    );
  }
  if (icon === 'people') {
    return (
      <View style={styles.glyphRoot} accessibilityElementsHidden>
        <View style={styles.peopleRow}>
          <View style={[styles.personHead, { borderColor: color }]} />
          <View style={[styles.personHead, { borderColor: color }]} />
        </View>
        <View style={styles.peopleBodies}>
          <View style={[styles.personBody, { borderColor: color }]} />
          <View style={[styles.personBody, { borderColor: color }]} />
        </View>
      </View>
    );
  }
  if (icon === 'clock') {
    return (
      <View style={styles.glyphRoot} accessibilityElementsHidden>
        <View style={[styles.clockRing, { borderColor: color }]}>
          <View style={[styles.clockHand, { backgroundColor: color }]} />
        </View>
      </View>
    );
  }
  if (icon === 'chart') {
    return (
      <View style={styles.glyphRoot} accessibilityElementsHidden>
        <View style={styles.chartRow}>
          <View style={[styles.chartBar, styles.chartBarSm, { backgroundColor: color }]} />
          <View style={[styles.chartBar, styles.chartBarMd, { backgroundColor: color }]} />
          <View style={[styles.chartBar, styles.chartBarLg, { backgroundColor: color }]} />
        </View>
      </View>
    );
  }
  if (icon === 'home') {
    return (
      <View style={styles.glyphRoot} accessibilityElementsHidden>
        <View style={[styles.homeRoof, { borderBottomColor: color }]} />
        <View style={[styles.homeBody, { borderColor: color }]} />
      </View>
    );
  }
  if (icon === 'leave') {
    return (
      <View style={styles.glyphRoot} accessibilityElementsHidden>
        <View style={[styles.doorFrame, { borderColor: color }]}>
          <View style={[styles.doorKnob, { backgroundColor: color }]} />
        </View>
      </View>
    );
  }
  // clipboard / document
  return (
    <View style={styles.glyphRoot} accessibilityElementsHidden>
      <View style={[styles.docBox, { borderColor: color }]}>
        <View style={[styles.docClip, { backgroundColor: color }]} />
        <View style={[styles.docLine, { backgroundColor: color }]} />
        <View style={[styles.docLine, styles.docLineShort, { backgroundColor: color }]} />
        <View style={[styles.docLine, styles.docLineMed, { backgroundColor: color }]} />
      </View>
    </View>
  );
}

function CategoryRow({
  item,
  palette,
  showDivider,
}: {
  item: CategoryItem;
  palette: ColorPalette;
  showDivider: boolean;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={item.title}
      onPress={item.onPress}
      style={({ pressed }) => [
        styles.row,
        {
          backgroundColor: palette.surface,
          borderBottomColor: palette.border,
          borderBottomWidth: showDivider ? StyleSheet.hairlineWidth : 0,
          opacity: pressed ? 0.72 : 1,
        },
      ]}
    >
      <View
        style={[styles.iconBadge, { backgroundColor: `${palette.info}14` }]}
        accessibilityElementsHidden
      >
        <CategoryGlyph icon={item.icon} color={palette.info} />
      </View>
      <Text style={[styles.rowTitle, { color: palette.textPrimary }]}>
        {item.title}
      </Text>
      <Text style={[styles.chevron, { color: palette.textMuted }]}>›</Text>
    </Pressable>
  );
}

function GreetingCard({
  displayName,
  palette,
}: {
  displayName: string;
  palette: ColorPalette;
}) {
  const isDark = palette.background === darkColors.background;
  return (
    <View
      style={[
        styles.greetingCard,
        {
          backgroundColor: isDark ? palette.brand : '#E8F1FF',
          borderColor: isDark ? palette.border : `${palette.info}22`,
        },
      ]}
      accessibilityRole="summary"
      accessibilityLabel={`${getGreeting()}, ${displayName}. ${formatDate(new Date())}`}
    >
      <View style={styles.greetingText}>
        <Text style={[styles.greetingTitle, { color: palette.textPrimary }]}>
          {getGreeting()},{' '}
          <Text style={styles.greetingName}>{displayName}</Text>
        </Text>
        <Text style={[styles.greetingDate, { color: palette.textSecondary }]}>
          {formatDate(new Date())}
        </Text>
      </View>
      <View style={[styles.greetingIcon, { backgroundColor: palette.info }]}>
        <CategoryGlyph icon="clipboard" color={palette.onInfo} />
      </View>
    </View>
  );
}

function QuickSummary({
  metrics,
  palette,
}: {
  metrics: AdminAttendanceDashboardMetrics | null;
  palette: ColorPalette;
}) {
  return (
    <View style={styles.section}>
      <Text style={[styles.sectionTitle, { color: palette.textPrimary }]}>
        Quick Summary
      </Text>
      <View style={styles.summaryGrid}>
        {QUICK_SUMMARY.map(def => {
          const valueColor = toneColor(palette, def.tone);
          const value = metrics?.[def.key];
          return (
            <View
              key={def.key}
              style={[
                styles.summaryCard,
                {
                  backgroundColor: palette.surface,
                  borderColor: palette.border,
                  shadowColor: palette.black,
                },
              ]}
              accessibilityRole="text"
              accessibilityLabel={`${def.label}: ${formatMetric(value)}`}
            >
              <Text style={[styles.summaryValue, { color: valueColor }]}>
                {formatMetric(value)}
              </Text>
              <Text
                style={[styles.summaryLabel, { color: palette.textSecondary }]}
                numberOfLines={2}
              >
                {def.label}
              </Text>
            </View>
          );
        })}
      </View>
    </View>
  );
}

function iconForReport(report: ReportDefinition): CategoryIcon {
  const code = report.reportCode.toUpperCase();
  if (code.includes('MONTHLY')) return 'calendar';
  if (code.includes('EMPLOYEE')) return 'people';
  if (code.includes('WFH')) return 'home';
  if (code.includes('LEAVE')) return 'leave';
  if (code.includes('PERMISSION')) return 'clock';
  if (code.includes('DAILY') || code.includes('ATTENDANCE')) return 'clipboard';
  return 'document';
}

export function ReportListScreen({ navigation }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const displayName = auth.user?.displayName || auth.user?.username || 'Admin';
  const canLoadSummary =
    auth.hasPermission(PERMISSIONS.ATTENDANCE_VIEW) &&
    auth.hasPermission(PERMISSIONS.EMPLOYEE_VIEW);
  const canOpenAttendanceReport =
    auth.hasPermission(PERMISSIONS.EMPLOYEE_VIEW) &&
    auth.hasAnyPermission([...REPORT_ACCESS_PERMISSIONS]);

  const [items, setItems] = useState<ReportDefinition[]>([]);
  const [access, setAccess] = useState<ReportAccess | null>(null);
  const [metrics, setMetrics] =
    useState<AdminAttendanceDashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reportsByCode = useMemo(() => {
    const map = new Map<string, ReportDefinition>();
    for (const report of items) {
      map.set(report.reportCode, report);
    }
    return map;
  }, [items]);

  const categories = useMemo((): CategoryItem[] => {
    const result: CategoryItem[] = [];
    // Role-restricted users (e.g. Employee) see only the report codes granted
    // to their role; their attendance data is limited to themselves by the API.
    const restricted = access?.restricted === true;
    const canOpenEntry = (accessCode: string) =>
      restricted ? isReportGranted(access, accessCode) : canOpenAttendanceReport;

    for (const def of DESIGN_REPORT_ORDER) {
      if (def.kind === 'attendance') {
        if (!canOpenEntry(def.accessCode)) continue;
        result.push({
          id: def.id,
          title: def.title,
          icon: def.icon,
          onPress: () =>
            navigation.navigate(
              routes.attendanceReport,
              def.attendanceStatus
                ? { initialStatus: def.attendanceStatus }
                : undefined,
            ),
        });
        continue;
      }

      if (def.kind === 'monthlyAttendance') {
        if (!canOpenEntry(def.accessCode)) continue;
        result.push({
          id: def.id,
          title: def.title,
          icon: def.icon,
          onPress: () => navigation.navigate(routes.monthlyAttendanceReport),
        });
        continue;
      }

      if (def.kind === 'weeklyAttendance') {
        if (!canOpenEntry(def.accessCode)) continue;
        result.push({
          id: def.id,
          title: def.title,
          icon: def.icon,
          onPress: () => navigation.navigate(routes.weeklyAttendanceReport),
        });
        continue;
      }

      if (def.kind === 'employeeOverallWeekly') {
        if (!canOpenAttendanceReport) continue;
        result.push({
          id: def.id,
          title: def.title,
          icon: def.icon,
          onPress: () => navigation.navigate(routes.employeeOverallWeeklyReport),
        });
        continue;
      }

      if (def.kind === 'employeeMonthlyAttendance') {
        if (!canOpenAttendanceReport) continue;
        result.push({
          id: def.id,
          title: def.title,
          icon: def.icon,
          onPress: () => navigation.navigate(routes.employeeMonthlyAttendanceReport),
        });
        continue;
      }

      if (def.kind === 'wfh') {
        if (
          !canOpenEntry(def.accessCode) ||
          !def.reportCode ||
          !reportsByCode.has(def.reportCode)
        ) {
          continue;
        }
        result.push({
          id: def.id,
          title: def.title,
          icon: def.icon,
          onPress: () => navigation.navigate(routes.wfhReport),
        });
        continue;
      }

      if (def.kind === 'leave') {
        if (
          !canOpenEntry(def.accessCode) ||
          !def.reportCode ||
          !reportsByCode.has(def.reportCode)
        ) {
          continue;
        }
        result.push({
          id: def.id,
          title: def.title,
          icon: def.icon,
          onPress: () => navigation.navigate(routes.leaveReport),
        });
        continue;
      }

      if (restricted && !isReportGranted(access, def.accessCode)) continue;
      if (!def.reportCode || !reportsByCode.has(def.reportCode)) continue;
      const code = def.reportCode;
      result.push({
        id: def.id,
        title: def.title,
        icon: def.icon,
        onPress: () =>
          navigation.navigate(routes.reportFilter, { reportCode: code }),
      });
    }

    for (const report of items) {
      if (COVERED_REPORT_CODES.has(report.reportCode)) continue;
      // Daily/attendance register is represented by Daily Attendance Report.
      if (
        report.reportCode === 'ATTENDANCE_REGISTER' ||
        report.reportCode === 'DAILY_ATTENDANCE_SUMMARY'
      ) {
        continue;
      }
      result.push({
        id: report.reportCode,
        title: report.reportName,
        icon: iconForReport(report),
        onPress: () =>
          navigation.navigate(routes.reportFilter, {
            reportCode: report.reportCode,
          }),
      });
    }

    return result;
  }, [access, canOpenAttendanceReport, items, navigation, reportsByCode]);

  const load = useCallback(
    async (isRefresh = false) => {
      isRefresh ? setRefreshing(true) : setLoading(true);
      try {
        const catalogPromise = getReportCatalogue();
        const metricsPromise = canLoadSummary
          ? getAdminAttendanceDashboard().catch(() => null)
          : Promise.resolve(null);

        const [catalogue, dashboard] = await Promise.all([
          catalogPromise,
          metricsPromise,
        ]);

        setItems(catalogue.definitions);
        setAccess(catalogue.access);
        setMetrics(dashboard?.metrics ?? null);
        setError(null);
      } catch (err) {
        setError(reportErrorMessage(err, 'Unable to load reports.'));
        if (!isRefresh) {
          setItems([]);
          setAccess(null);
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [canLoadSummary],
  );

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  const headerRight = (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel="Unread notifications"
      onPress={() => navigation.navigate(routes.notificationList)}
      hitSlop={8}
    >
      <BellIcon color={palette.white} />
    </Pressable>
  );

  const showInitialLoader = loading && !items.length && categories.length === 0;
  const showInitialError = Boolean(error) && !items.length && categories.length === 0;

  return (
    <PermissionGuard
      anyPermissions={[...REPORT_ACCESS_PERMISSIONS]}
      fallback={<UnauthorizedScreen />}
    >
      {showInitialLoader ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Reports"
            onBack={navigation.goBack}
            right={headerRight}
          />
          <AppLoader message="Loading reports" />
        </AppScreen>
      ) : showInitialError ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Reports"
            onBack={navigation.goBack}
            right={headerRight}
          />
          <ErrorState message={error!} onRetry={() => load()} />
        </AppScreen>
      ) : (
        <AppScreen
          scroll
          webLayout="wide"
          backgroundColor={palette.surface}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => load(true)}
              tintColor={palette.primary}
            />
          }
          contentStyle={styles.screen}
        >
          <ScreenHeader
            title="Reports"
            onBack={navigation.goBack}
            right={headerRight}
          />

          <View style={styles.body}>
            <GreetingCard displayName={displayName} palette={palette} />

            {canLoadSummary ? (
              <QuickSummary metrics={metrics} palette={palette} />
            ) : null}

            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { color: palette.textPrimary }]}>
                Report Categories
              </Text>

              {error ? (
                <ErrorState message={error} onRetry={() => load()} />
              ) : null}

              {!categories.length ? (
                <EmptyState
                  title="No reports"
                  message="No reports are available for your account."
                />
              ) : (
                <AppCard style={styles.listCard}>
                  {categories.map((item, index) => (
                    <CategoryRow
                      key={item.id}
                      item={item}
                      palette={palette}
                      showDivider={index < categories.length - 1}
                    />
                  ))}
                </AppCard>
              )}
            </View>
          </View>
        </AppScreen>
      )}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: { paddingBottom: spacing.xl },
  center: { justifyContent: 'center' },
  body: {
    marginTop: spacing.lg,
    gap: spacing.xl,
  },
  greetingCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderRadius: 16,
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.lg,
    gap: spacing.md,
  },
  greetingText: {
    flex: 1,
    gap: spacing.xs,
  },
  greetingTitle: {
    ...typography.sectionTitle,
    fontWeight: '600',
  },
  greetingName: {
    fontWeight: '800',
  },
  greetingDate: {
    ...typography.body,
    opacity: 0.85,
  },
  greetingIcon: {
    width: 52,
    height: 52,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  section: {
    gap: spacing.md,
  },
  sectionTitle: {
    ...typography.sectionTitle,
    fontWeight: '700',
  },
  summaryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    rowGap: spacing.md,
  },
  summaryCard: {
    width: '48%',
    borderWidth: 1,
    borderRadius: 14,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    gap: spacing.xs,
    minHeight: 92,
    justifyContent: 'center',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 2,
  },
  summaryValue: {
    ...typography.screenTitle,
    fontSize: 28,
    lineHeight: 34,
    fontWeight: '700',
  },
  summaryLabel: {
    ...typography.caption,
    fontWeight: '600',
  },
  listCard: {
    paddingVertical: 0,
    paddingHorizontal: 0,
    overflow: 'hidden',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.lg,
    gap: spacing.md,
  },
  iconBadge: {
    width: 40,
    height: 40,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  glyphRoot: {
    width: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  docBox: {
    width: 16,
    height: 20,
    borderWidth: 1.5,
    borderRadius: 3,
    paddingHorizontal: 2,
    paddingTop: 5,
    gap: 2.5,
    alignItems: 'flex-start',
  },
  docClip: {
    position: 'absolute',
    top: -3,
    alignSelf: 'center',
    left: 3,
    width: 8,
    height: 4,
    borderRadius: 1,
  },
  docLine: {
    height: 1.5,
    borderRadius: 1,
    width: '100%',
  },
  docLineShort: { width: '55%' },
  docLineMed: { width: '75%' },
  calendarBox: {
    width: 16,
    height: 16,
    borderWidth: 1.5,
    borderRadius: 3,
    overflow: 'hidden',
  },
  calendarTop: {
    height: 4,
    width: '100%',
  },
  calendarDots: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-evenly',
    paddingHorizontal: 1,
  },
  calendarDot: {
    width: 2.5,
    height: 2.5,
    borderRadius: 1.5,
  },
  peopleRow: {
    flexDirection: 'row',
    gap: 3,
  },
  peopleBodies: {
    flexDirection: 'row',
    gap: 2,
    marginTop: 1,
  },
  personHead: {
    width: 7,
    height: 7,
    borderRadius: 4,
    borderWidth: 1.5,
  },
  personBody: {
    width: 9,
    height: 6,
    borderTopLeftRadius: 5,
    borderTopRightRadius: 5,
    borderWidth: 1.5,
  },
  clockRing: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: 3,
  },
  clockHand: {
    width: 1.5,
    height: 5,
    borderRadius: 1,
  },
  chartRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 2,
    height: 16,
  },
  chartBar: {
    width: 3.5,
    borderRadius: 1,
  },
  chartBarSm: { height: 7 },
  chartBarMd: { height: 11 },
  chartBarLg: { height: 16 },
  homeRoof: {
    width: 0,
    height: 0,
    borderLeftWidth: 8,
    borderRightWidth: 8,
    borderBottomWidth: 6,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    marginBottom: 1,
  },
  homeBody: {
    width: 12,
    height: 9,
    borderWidth: 1.5,
    borderTopWidth: 0,
  },
  doorFrame: {
    width: 12,
    height: 16,
    borderWidth: 1.5,
    borderRadius: 2,
    justifyContent: 'center',
    paddingLeft: 7,
  },
  doorKnob: {
    width: 2,
    height: 2,
    borderRadius: 1,
  },
  rowTitle: {
    ...typography.body,
    flex: 1,
    fontWeight: '500',
  },
  chevron: {
    fontSize: 28,
    lineHeight: 28,
    fontWeight: '300',
    marginTop: -2,
  },
  bellRoot: {
    width: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  bellBody: {
    width: 14,
    height: 14,
    borderWidth: 2,
    borderTopLeftRadius: 7,
    borderTopRightRadius: 7,
    borderBottomLeftRadius: 3,
    borderBottomRightRadius: 3,
    marginTop: 1,
  },
  bellClapper: {
    width: 4,
    height: 4,
    borderRadius: 2,
    marginTop: 1,
  },
});
